import React from 'react';
import { Tag } from 'antd';
import { useAuthStore } from '@/store/authStore';
import { PRINT_PLAIN_AMOUNT_PROFILE, formatMoney } from '@/utils/format';
import { RECOGNITION_PERMISSIONS, REVISION_PERMISSION, type RevisionType } from '@/services/financeRecognition.service';

const STATUS_COLOR: Record<string, string> = {
  DRAFT: 'default',
  PENDING_APPROVAL: 'gold',
  APPROVED: 'cyan',
  SCHEDULED: 'geekblue',
  ACTIVE: 'green',
  SUSPENDED: 'orange',
  TERMINATING: 'volcano',
  TERMINATED: 'red',
  FULLY_RECOGNIZED: 'purple',
  CLOSED: 'default',
  CANCELLED: 'red',
  PLANNED: 'default',
  DUE: 'blue',
  DRAFT_JV_CREATED: 'cyan',
  POSTED: 'green',
  PARTIALLY_POSTED: 'lime',
  HELD: 'orange',
  SKIPPED: 'default',
  REVERSED: 'magenta',
  POSTING_BLOCKED: 'red',
  VALIDATED: 'cyan',
  PROCESSING: 'processing',
  PARTIALLY_COMPLETED: 'orange',
  COMPLETED: 'green',
  FAILED: 'red',
  APPLIED: 'green',
  REJECTED: 'red',
  READY: 'blue',
  DRAFT_CREATED: 'cyan',
  EXCEPTION: 'red',
  PENDING: 'default',
  RUNNING: 'processing',
  PARTIAL: 'orange',
  DISCARDED: 'default',
};

/** `FULLY_RECOGNIZED` → `Fully recognized`. */
export function humanize(code: string | null | undefined): string {
  if (!code) return '—';
  const s = String(code).replace(/_/g, ' ').toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export const StatusTag: React.FC<{ status: string | null | undefined }> = ({ status }) =>
  status ? <Tag color={STATUS_COLOR[status] ?? 'default'}>{humanize(status)}</Tag> : <>—</>;

/** Formats an exact decimal string for display (2 dp, grouped); never used for arithmetic. */
export function amount(value: string | number | null | undefined, currency?: string | null): string {
  if (value === null || value === undefined || value === '') return '—';
  if (!Number.isFinite(Number(value))) return String(value);
  return currency
    ? formatMoney(value, { ...PRINT_PLAIN_AMOUNT_PROFILE, display: 'code_prefix', currency, separator: ' ' })
    : formatMoney(value, PRINT_PLAIN_AMOUNT_PROFILE);
}

/** Exact 2-dp decimal string arithmetic (cents as BigInt) for client-side previews. */
export function decimalSub(a: string | number | null | undefined, b: string | number | null | undefined): string {
  const toCents = (v: string | number | null | undefined) => {
    const s = String(v ?? '0').trim() || '0';
    const neg = s.startsWith('-');
    const [i, f = ''] = s.replace(/^[-+]/, '').split('.');
    const cents = BigInt(i || '0') * BigInt(100) + BigInt((f + '00').slice(0, 2));
    return neg ? -cents : cents;
  };
  const d = toCents(a) - toCents(b);
  const neg = d < BigInt(0);
  const abs = neg ? -d : d;
  const units = abs / BigInt(100);
  const cents = (abs % BigInt(100)).toString().padStart(2, '0');
  return `${neg ? '-' : ''}${units}.${cents}`;
}

/** JSON columns arrive as objects or (MariaDB raw queries) as JSON text. */
export function asObject(value: unknown): Record<string, unknown> {
  if (value && typeof value === 'object') return value as Record<string, unknown>;
  if (typeof value === 'string' && value.trim().startsWith('{')) {
    try {
      return JSON.parse(value) as Record<string, unknown>;
    } catch {
      return {};
    }
  }
  return {};
}

export function accountLabel(code?: string | null, name?: string | null): string {
  if (!code && !name) return '—';
  return [code, name].filter(Boolean).join(' - ');
}

export const jvPath = (voucherId: string) => `/fin/gl/journals?vid=${voucherId}`;
export const contractPath = (id: string) => `/fin/prepaid/contracts/${id}`;

export function apiErrorMessage(e: unknown, fallback = 'Request failed'): string {
  const ax = e as { response?: { data?: { message?: string } }; message?: string };
  return ax?.response?.data?.message || ax?.message || fallback;
}

export function useRecognitionAccess() {
  const user = useAuthStore((s) => s.user);
  const perms: string[] = user?.permissions ?? [];
  const isSuperAdmin = Boolean(user?.is_super_admin);
  const has = (code: string) => isSuperAdmin || perms.includes(code);
  return {
    isSuperAdmin,
    has,
    canView: has(RECOGNITION_PERMISSIONS.VIEW),
    canCreate: has(RECOGNITION_PERMISSIONS.CREATE),
    canEdit: has(RECOGNITION_PERMISSIONS.EDIT),
    canSubmit: has(RECOGNITION_PERMISSIONS.SUBMIT),
    canApprove: has(RECOGNITION_PERMISSIONS.APPROVE),
    canActivate: has(RECOGNITION_PERMISSIONS.ACTIVATE),
    canRun: has(RECOGNITION_PERMISSIONS.RUN),
    canPost: has(RECOGNITION_PERMISSIONS.POST),
    canReconcile: has(RECOGNITION_PERMISSIONS.RECONCILE),
    canAdmin: has(RECOGNITION_PERMISSIONS.ADMIN),
    canAdjust: (type: RevisionType) => has(REVISION_PERMISSION[type]),
    canAdjustAny: (Object.values(REVISION_PERMISSION) as string[]).some(has),
  };
}

export const METHOD_LABEL: Record<string, string> = {
  DAILY_PRO_RATA: 'Daily pro-rata',
  STRAIGHT_LINE_MONTHLY: 'Straight-line monthly',
  EQUAL_ACCOUNTING_PERIOD: 'Equal per accounting period',
  CUSTOM_SCHEDULE: 'Custom schedule',
};

export const DAY_COUNT_LABEL: Record<string, string> = {
  ACTUAL_CALENDAR_DAYS: 'Actual calendar days',
  ACTUAL_365: 'Actual / 365',
  ACTUAL_366: 'Actual / 366',
  THIRTY_DAY_MONTH: '30-day month',
  ACCOUNTING_PERIOD_DAYS: 'Accounting period days',
};

export const POSTING_MODE_LABEL: Record<string, string> = {
  MANUAL: 'Manual',
  AUTO_CREATE_DRAFT: 'Auto-create draft JV',
  AUTO_POST: 'Auto-post JV',
};

export const options = (values: readonly string[], labels?: Record<string, string>) =>
  values.map((v) => ({ value: v, label: labels?.[v] ?? humanize(v) }));
